// Core
// Vendors
import { useTranslations } from '@/hooks/useTranslations';
import { Check, ChevronsUpDown } from 'lucide-react';
// Utils
import { cn } from '@/lib/utils';
// UI components
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useState } from 'react';

type TInvoiceType = {
  value: string;
  label: string;
};

interface IInvoiceType {
  value: string;
  onSelect: (value: string) => void;
  placeholder: string;
}

export function InvoiceType({ value, onSelect, placeholder }: IInvoiceType) {
  const t = useTranslations('CreateInvoicePopup');
  const enums = useTranslations('Enums.invoiceType');

  const invoiceType: TInvoiceType[] = [
    {
      value: 'Invoice',
      label: enums('invoice'),
    },
    {
      value: 'Proforma Invoice',
      label: enums('proformaInvoice'),
    },
    {
      value: 'Offer',
      label: enums('offer'),
    },
    {
      value: 'E-commerce',
      label: enums('ecommerce'),
    },
  ];
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          aria-label="Invoice type"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {value
            ? invoiceType.find((type) => type.value === value)?.label
            : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandInput placeholder={t('searchPlaceholder')} />
          <CommandList>
            <CommandEmpty>{t('noResults')}</CommandEmpty>
            <CommandGroup>
              {invoiceType.map((type) => (
                <CommandItem
                  key={type.value}
                  value={type.value}
                  onSelect={(currentValue) => {
                    onSelect(currentValue === value ? '' : currentValue);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === type.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {type.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
